"use client";

import useSWR, { mutate as globalMutate } from "swr";
import { useAuth } from "@clerk/nextjs";
import { useCallback } from "react";
import { createFetcher } from "@/lib/api";
import type {
  Load,
  LoadsResponse,
  LoadStatus,
  DeliveryMethod,
} from "@/lib/api-types";

export function useLoads(
  page = 1,
  limit = 25,
  filters?: {
    status?: LoadStatus;
    driver_id?: string;
    delivery_method?: DeliveryMethod;
    q?: string;
  },
) {
  const { getToken } = useAuth();
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (filters?.status) params.set("status", filters.status);
  if (filters?.driver_id) params.set("driver_id", filters.driver_id);
  if (filters?.delivery_method)
    params.set("delivery_method", filters.delivery_method);
  if (filters?.q) params.set("q", filters.q);

  const { data, error, isLoading } = useSWR<LoadsResponse>(
    `/api/v1/loads?${params}`,
    createFetcher(getToken),
    { refreshInterval: 5_000 },
  );
  return {
    loads: data?.loads ?? [],
    total: data?.total ?? 0,
    page: data?.page ?? page,
    isLoading,
    error,
  };
}

export function useActiveLoads() {
  return useLoads(1, 100);
}

export function useLoad(id: string | undefined) {
  const { getToken } = useAuth();
  const { data, error, isLoading } = useSWR<Load>(
    id ? `/api/v1/loads/${id}` : null,
    createFetcher(getToken),
  );
  return { load: data, isLoading, error };
}

// isComingled returns true when the load's shipments span more than one
// distinct customer (via their parent orders). Computed client-side from
// the hydrated `shipments` array on a single-load fetch — list endpoints
// don't include the join so this only works on `useLoad`.
export function isComingled(load: Load | undefined): boolean {
  if (!load?.shipments || load.shipments.length < 2) return false;
  const orderIds = new Set(load.shipments.map((s) => s.order_id));
  return orderIds.size > 1;
}

export function useLoadMutations() {
  const { getToken } = useAuth();

  const create = useCallback(
    async (input: {
      shipment_ids: string[];
      driver_id?: string;
      vehicle_id?: string;
      delivery_method?: DeliveryMethod;
      carrier_type?: string;
    }) => {
      const token = await getToken();
      const res = await fetch("/api/v1/loads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      await globalMutate((key: unknown) =>
        typeof key === "string" && key.startsWith("/api/v1/loads"),
      );
      return (await res.json()) as Load;
    },
    [getToken],
  );

  const updateStatus = useCallback(
    async (id: string, status: LoadStatus) => {
      const token = await getToken();
      const res = await fetch(`/api/v1/loads/${id}/status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      await globalMutate((key: unknown) =>
        typeof key === "string" && key.startsWith("/api/v1/loads"),
      );
    },
    [getToken],
  );

  const cancel = useCallback(
    async (id: string) => {
      const token = await getToken();
      const res = await fetch(`/api/v1/loads/${id}/cancel`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error((await res.json()).error);
      await globalMutate((key: unknown) =>
        typeof key === "string" && key.startsWith("/api/v1/loads"),
      );
    },
    [getToken],
  );

  return { create, updateStatus, cancel };
}
